/**
 * Styled dashboard view for icon navigation (transactions, physical records, day closure).
 */
import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  InputBase,
  Stack,
  Typography,
} from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import useAdvisorTransactionsContent from "../../../../pages/AdministrationPanel/Pages/AdvisorPanel/hooks/useAdvisorTransactionsContent";
import usePhysicalRegistration from "../../../../pages/AdministrationPanel/Pages/Analytics/AnalyticsContext/hooks/PhysicalRegistration/usePhysicalRegistration";
import DayClousure from "../../../../pages/AdministrationPanel/Pages/AdvisorPanel/Components/DayClousure/DayClousure";
import AuthorizationComponent from "../../../../pages/AdministrationPanel/Pages/AdvisorPanel/Components/AuthorizationComponent/AuthorizationComponent";
import { useAuth } from "../../../../context/AuthContext/AuthContext";
import { logDb, payloadsDB } from "../../../../configurations/DataBase/bases";

type DashboardProps = {
  paymentMode?: boolean;
};

type DashboardTab = "transactions" | "physical" | "dayclose";
type DashboardAuthorization = {
  openphisicalregist: { state: boolean; user: any };
  openclosingday: { state: boolean; user: any };
};

type MetricCardProps = {
  title: string;
  value: number | string;
  subtitle: string;
  fullWidth?: boolean;
  maxWidthMobile?: string;
};

const MetricCard = ({
  title,
  value,
  subtitle,
  fullWidth = false,
  maxWidthMobile,
}: MetricCardProps) => (
  <Box
    sx={{
      alignItems: "center",
      backgroundColor: "#F9F9F9",
      borderRadius: "8px",
      boxShadow:
        "0px 6px 12px -6px rgba(19, 25, 39, 0.12), 0px 8px 24px -4px rgba(19, 25, 39, 0.08)",
      display: "flex",
      flexDirection: "column",
      gap: "4px",
      height: { xs: "122px", md: "128px" },
      justifyContent: "center",
      mx: "auto",
      minWidth: 0,
      px: { xs: 1.5, md: 2 },
      textAlign: "center",
      width: fullWidth
        ? { xs: "100%", md: "667px" }
        : { xs: "calc(50% - 5px)", md: "326px" },
      maxWidth: { xs: maxWidthMobile ?? "none", md: "none" },
    }}
  >
    <Typography
      sx={{
        color: "#761542",
        fontFamily: "Inter, sans-serif",
        fontSize: { xs: "14px", md: "16px" },
        fontWeight: 600,
        lineHeight: { xs: "18px", md: "20px" },
      }}
    >
      {title}
    </Typography>
    <Typography
      sx={{
        color: "#00596A",
        fontFamily: "Inter, sans-serif",
        fontSize: { xs: "30px", md: "34px" },
        fontWeight: 600,
        lineHeight: { xs: "28px", md: "30px" },
      }}
    >
      {value}
    </Typography>
    <Typography
      sx={{
        color: "#232323",
        fontFamily: "Inter, sans-serif",
        fontSize: { xs: "13px", md: "14px" },
        fontWeight: 400,
        lineHeight: { xs: "18px", md: "20px" },
      }}
    >
      {subtitle}
    </Typography>
  </Box>
);

const Dashboard = ({ paymentMode = false }: DashboardProps) => {
  const [activeTab, setActiveTab] = useState<DashboardTab>("transactions");
  const [searchTerm, setSearchTerm] = useState("");
  const [authorization, setAuthorization] = useState<DashboardAuthorization>({
    openphisicalregist: { state: false, user: "" },
    openclosingday: { state: false, user: "" },
  });
  const [openDayClosureAuthorization, setOpenDayClosureAuthorization] =
    useState(false);

  const { user } = useAuth();
  const transactions = useAdvisorTransactionsContent();
  const physical = usePhysicalRegistration({ user: user?.id ?? null });

  useEffect(() => {
    if (activeTab === "dayclose" && !authorization.openclosingday.state) {
      setOpenDayClosureAuthorization(true);
    }
  }, [activeTab, authorization.openclosingday.state]);

  const transactionMetrics = useMemo(() => {
    const cards = transactions.TransactionsCards;
    return {
      total:
        cards.TransactionsUsersOBJ.numericvalue ??
        cards.TransactionsUsersOBJ.Value ??
        0,
      pax: cards.PaxOBJ.numericvalue ?? cards.PaxOBJ.Value ?? 0,
      manual:
        cards.ManualTransaction.numericvalue ?? cards.ManualTransaction.Value ?? 0,
      digital:
        cards.DigitalTransaction.numericvalue ??
        cards.DigitalTransaction.Value ??
        0,
      voucher: physical.physicalcontent.PayementsVouchersCompleted.totalSum ?? 0,
    };
  }, [
    transactions.TransactionsCards,
    physical.physicalcontent.PayementsVouchersCompleted.totalSum,
  ]);

  const physicalMetrics = useMemo(() => {
    return {
      noImage: physical.physicalcontent.PayementsVouchers.totalSum ?? 0,
      voucher: physical.physicalcontent.PayementsVouchersCompleted.totalSum ?? 0,
    };
  }, [
    physical.physicalcontent.PayementsVouchers.totalSum,
    physical.physicalcontent.PayementsVouchersCompleted.totalSum,
  ]);

  const handleRefresh = () => {
    transactions.makeRequest();
    physical.physicalcontent.makeRequest();
  };

  const handleDownloadLogs = async () => {
    const logs = await logDb.documents.toArray();
    const blob = new Blob([JSON.stringify(logs, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "logs.txt";
    document.body.appendChild(a);
    a.click();
    if (document.body.contains(a)) document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDownloadPayloads = async () => {
    const payloads = await payloadsDB.documents.toArray();
    const blob = new Blob([JSON.stringify(payloads, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "payloads.txt";
    document.body.appendChild(a);
    a.click();
    if (document.body.contains(a)) document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

const tabStyle = (tab: DashboardTab) => ({
    borderBottom:
      activeTab === tab ? "2px solid #00596A" : "2px solid transparent",
    color: activeTab === tab ? "#00596A" : "#545454",
    cursor: "pointer",
    fontFamily: "Inter, sans-serif",
    fontSize: { xs: "12px", md: "18px" },
    fontWeight: 600,
    lineHeight: { xs: "16px", md: "20px" },
    pb: "4px",
    whiteSpace: "nowrap",
  });

  return (
    <Box
      sx={{
        ...(paymentMode
          ? {
              bgcolor: "#F6F6F6",
              height: "100%",
              minHeight: "100%",
              overflow: "auto",
              overflowX: "hidden",
              position: "relative",
              width: "100%",
              maxWidth: "100%",
              zIndex: 1,
            }
          : {
              bgcolor: "white",
              height: "100vh",
              left: 0,
              overflow: "auto",
              overflowX: "hidden",
              position: "fixed",
              top: 0,
              width: "100%",
              zIndex: 10,
            }),
      }}
    >
      <Box
        sx={{
          margin: "0 auto",
          maxWidth: "667px",
          pb: 16,
          pt: { xs: "90px", md: "71px" },
          px: { xs: 2, md: 0 },
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <Stack
          direction="row"
          spacing={{ xs: 1.5, md: "25px" }}
          sx={{ flexWrap: "wrap", rowGap: 1, columnGap: { xs: 1, md: 0 } }}
        >
          <Typography
            sx={tabStyle("transactions")}
            onClick={() => setActiveTab("transactions")}
          >
            TRANSACCIONES
          </Typography>
          <Typography
            sx={tabStyle("physical")}
            onClick={() => setActiveTab("physical")}
          >
            REGISTROS FÍSICOS
          </Typography>
          <Typography
            sx={tabStyle("dayclose")}
            onClick={() => setActiveTab("dayclose")}
          >
            CIERRE DE DÍA
          </Typography>
        </Stack>

        <Box
          sx={{
            alignItems: "center",
            display: "grid",
            gridTemplateColumns: { xs: "minmax(0, 1fr) auto", md: "1fr auto" },
            gap: 2,
            mt: "14px",
            width: "100%",
          }}
        >
          <Box
            sx={{
              alignItems: "center",
              border: "1.3px solid #888",
              borderRadius: "8px",
              display: "flex",
              height: "34px",
              minWidth: 0,
              px: "12px",
              width: { xs: "100%", md: "200px" },
            }}
          >
            <InputBase
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar"
              sx={{
                color: "#545454",
                fontFamily: "Inter, sans-serif",
                fontSize: "14px",
                lineHeight: "20px",
                width: "100%",
              }}
            />
            <SearchOutlinedIcon sx={{ color: "#232323", fontSize: 20 }} />
          </Box>

          <IconButton
            onClick={handleRefresh}
            sx={{ border: "1.5px solid #943663", color: "#943663", height: "32px", justifySelf: "end", width: "32px" }}
          >
            <RefreshOutlinedIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        <Stack
          spacing="12px"
          sx={{
            mt: "18px",
            alignItems: { xs: "center", md: "stretch" },
            justifyContent: { xs: "center", md: "flex-start" },
          }}
        >
          {activeTab === "transactions" && (
            <>
              <Stack direction="row" spacing="10px">
                <MetricCard
                  title="TRANSACCIONES"
                  value={transactionMetrics.total}
                  subtitle="Total de transacciones hoy"
                />
                <MetricCard
                  title="PASAJEROS"
                  value={transactionMetrics.pax}
                  subtitle="Total de pasajeros registrados hoy"
                />
              </Stack>
              <MetricCard
                title="TRANSACCIONES MANUALES"
                value={transactionMetrics.manual}
                subtitle="Total de transacciones manuales hoy"
                fullWidth
                maxWidthMobile="100%"
              />
              <MetricCard
                title="TRANSACCIONES DIGITALES"
                value={transactionMetrics.digital}
                subtitle="Total de transacciones digitales hoy"
                fullWidth
                maxWidthMobile="100%"
              />
              <MetricCard
                title="TRANSACCIONES POR VOUCHER"
                value={transactionMetrics.voucher}
                subtitle="Total de transacciones manuales hoy"
                fullWidth
                maxWidthMobile="100%"
              />
            </>
          )}

          {activeTab === "physical" && (
            <>
              <MetricCard
                title="TRANSACCIONES F�SICAS SIN IMAGEN"
                value={physicalMetrics.noImage}
                subtitle="Total de transacciones manuales hoy"
                fullWidth
              />
              <MetricCard
                title="TRANSACCIONES POR VOUCHER"
                value={physicalMetrics.voucher}
                subtitle="Total de transacciones manuales hoy"
                fullWidth
              />
            </>
          )}

          {activeTab === "dayclose" && (
            <Box
              sx={{
                backgroundColor: "#F9F9F9",
                borderRadius: "8px",
                boxShadow:
                  "0px 6px 12px -6px rgba(19, 25, 39, 0.12), 0px 8px 24px -4px rgba(19, 25, 39, 0.08)",
                minHeight: "540px",
                overflow: "hidden",
                p: 1,
                width: { xs: "100%", md: "667px" },
                boxSizing: "border-box",
              }}
            >
              <Box sx={{ height: "100%", maxHeight: "650px", overflow: "auto", pr: 1 }}>
                <DayClousure authorization={authorization} />
              </Box>
            </Box>
          )}
        </Stack>

        <Stack direction="row" spacing="10px" sx={{ mt: "12px", flexWrap: "nowrap", rowGap: 1 }}>
          <Button
            onClick={handleDownloadLogs}
            startIcon={<FileDownloadOutlinedIcon />}
            variant="outlined"
            sx={{
              border: "1.5px solid #943663",
              borderRadius: "24px",
              color: "#943663",
              fontFamily: "Inter, sans-serif",
              fontSize: { xs: "11px", md: "12px" },
              fontWeight: 600,
              height: "32px",
              px: { xs: 1.25, md: 2 },
              whiteSpace: "nowrap",
              width: { xs: "50%", md: "auto" },
              textTransform: "none",
            }}
          >
            Registro de errores
          </Button>
          <Button
            onClick={handleDownloadPayloads}
            variant="outlined"
            sx={{
              border: "1.5px solid #943663",
              borderRadius: "24px",
              color: "#943663",
              fontFamily: "Inter, sans-serif",
              fontSize: { xs: "11px", md: "12px" },
              fontWeight: 600,
              height: "32px",
              px: { xs: 1.25, md: 2 },
              whiteSpace: "nowrap",
              width: { xs: "50%", md: "auto" },
              textTransform: "none",
            }}
          >
            Información de formulario
          </Button>
        </Stack>
      </Box>

      <AuthorizationComponent
        onAutorizationDone={(auth: any) => {
          if (auth.state && !authorization.openclosingday.state) {
            setAuthorization((prev) => ({
              ...prev,
              openclosingday: auth,
            }));
            setOpenDayClosureAuthorization(false);
          }
        }}
        open={openDayClosureAuthorization}
        onClose={() => {
          if (!authorization.openclosingday.state) {
            setOpenDayClosureAuthorization(false);
            setActiveTab("transactions");
          }
        }}
      />
    </Box>
  );
};

export default Dashboard;
